// @file Allow two fingers to pinch an image on mobile device to zoom and move
// while the body stay still, and one finger to drag the image when it is zoomed.

enum PinchZoomTouchType {
  None,
  Single,
  Multi,
}
const initPinchZoom = (pinchZoomElement: HTMLElement): (() => void) => {
  let pinchZoomElementScale = 1
  let newPinchZoomElementScale = 1
  const start: { x: number; y: number; distance: number } = { x: 0, y: 0, distance: 0 }

  let deltaX = 0
  let deltaY = 0
  let newDeltaX = 0
  let newDeltaY = 0
  let currentTouchType: PinchZoomTouchType = PinchZoomTouchType.None

  /**
   * Calculate distance between two fingers for the TouchEvent
   */
  const distance = (event: TouchEvent): number => {
    return Math.hypot(event.touches[0].pageX - event.touches[1].pageX, event.touches[0].pageY - event.touches[1].pageY)
  }

  /**
   * Calculate how much the fingers have zoomed for the TouchEvent
   */
  const computeScale = (event: TouchEvent & { scale?: number }): number => {
    let scale = 0
    // Some browsers provides event.scale as two fingers move on the screen
    // For other browsers just calculate the scale manually
    if (event.scale) {
      scale = event.scale
    } else {
      const deltaDistance = distance(event)
      scale = deltaDistance / start.distance
    }

    return Math.min(Math.max(1, scale * pinchZoomElementScale), 4)
  }

  /**
   * Calculate how much the fingers have moved on the X and Y axis
   */
  const computeDelta = (currentX: number, currentY: number): { dx: number; dy: number } => {
    let dx = deltaX + currentX - start.x
    let dy = deltaY + currentY - start.y
    // Maximum delta is calculated based on the image size and the current scale
    // so that user will not be able to drag the image too far.
    const maxDx = (pinchZoomElement.offsetWidth * (newPinchZoomElementScale - 1)) / 2
    const maxDy = (pinchZoomElement.offsetHeight * (newPinchZoomElementScale - 1)) / 2
    dx = Math.sign(dx) * Math.min(Math.abs(dx), maxDx)
    dy = Math.sign(dy) * Math.min(Math.abs(dy), maxDy)
    return { dx, dy }
  }

  const touchStartHandler = (event): void => {
    pinchZoomElementScale = newPinchZoomElementScale
    deltaX = newDeltaX
    deltaY = newDeltaY
    currentTouchType = PinchZoomTouchType.None
    if (event.touches.length === 2) {
      event.preventDefault() // Prevent page scroll and initPinchZoom
      // Calculate where the fingers have started on the X and Y axis
      start.x = (event.touches[0].pageX + event.touches[1].pageX) / 2
      start.y = (event.touches[0].pageY + event.touches[1].pageY) / 2
      start.distance = distance(event)
      currentTouchType = PinchZoomTouchType.Multi
    }
    if (event.touches.length === 1) {
      event.preventDefault() // Prevent page scroll
      // Calculate where the fingers have started on the X and Y axis
      start.x = event.touches[0].pageX
      start.y = event.touches[0].pageY
      start.distance = 0
      currentTouchType = PinchZoomTouchType.Single
    }
  }

  const touchMoveHandler = (event): void => {
    if (currentTouchType === PinchZoomTouchType.Multi && event.touches.length === 2) {
      event.preventDefault() // Prevent page scroll
      newPinchZoomElementScale = computeScale(event)
      const currentX = (event.touches[0].pageX + event.touches[1].pageX) / 2
      const currentY = (event.touches[0].pageY + event.touches[1].pageY) / 2
      const newDeltas = computeDelta(currentX, currentY)
      newDeltaX = newDeltas.dx
      newDeltaY = newDeltas.dy
    }

    if (currentTouchType === PinchZoomTouchType.Single && event.touches.length === 1) {
      event.preventDefault() // Prevent page scroll
      newPinchZoomElementScale = pinchZoomElementScale // original scale, no change
      const currentX = event.touches[0].pageX
      const currentY = event.touches[0].pageY
      const newDeltas = computeDelta(currentX, currentY)
      newDeltaX = newDeltas.dx
      newDeltaY = newDeltas.dy
    }

    // Finally, transform the image to make it grow and move with fingers
    const transform = `translate3d(${newDeltaX}px, ${newDeltaY}px, 0) scale(${newPinchZoomElementScale})`
    pinchZoomElement.style.transform = transform
    pinchZoomElement.style.webkitTransform = transform
  }

  pinchZoomElement.addEventListener('touchstart', touchStartHandler)
  pinchZoomElement.addEventListener('touchmove', touchMoveHandler)

  const removeListeners = (): void => {
    pinchZoomElement.removeEventListener('touchstart', touchStartHandler)
    pinchZoomElement.removeEventListener('touchmove', touchMoveHandler)
  }
  return removeListeners
}

export default initPinchZoom
